/* ────────────────────────────────────────────────────────────────
   Kushface — Site Nav reveal
   Pops the yellow jump-nav in the moment the broadcast bar (the first
   test-card strip, directly below the hero) comes into view — i.e. once
   the hero scroll choreography is done — and hides it again on scroll
   back into the hero. IntersectionObserver only — no GSAP, no
   ScrollTrigger pin (respects the one-pinned-section rule).
   ──────────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  // Scrollspy: redden the jump link whose target section is currently
  // under the fixed nav. Also reddens immediately on click. Page-level
  // active links (HOME/FLOWER) are set server-side and have no hash, so
  // they are left untouched here.
  function initSpy(nav) {
    var links = Array.prototype.slice.call(
      document.querySelectorAll(".kf-sitenav__link")
    );
    // Page-level active link (HOME / FLOWER) is set server-side and has
    // no hash. Only one nav link should read as active at a time, so when
    // a jump target is in view we drop the page-level active, and restore
    // it when scrolled back above every target.
    var pageActive = document.querySelector(".kf-sitenav__link.is-active");

    var map = [];
    links.forEach(function (link) {
      var hash = link.hash || "";
      if (!hash || hash.charAt(0) !== "#") return;
      var el = document.getElementById(hash.slice(1));
      if (el) map.push({ link: link, el: el });
    });
    if (!map.length) return;

    function setPageActive(on) {
      if (pageActive) pageActive.classList.toggle("is-active", on);
    }

    function sync() {
      // The active section is the one whose top is closest to (but not
      // past) the nav line — i.e. the largest top that is still <= 120.
      // Selecting by "last in the list" instead breaks when nav order
      // doesn't match document order (WHOLESALE precedes STORES in the
      // nav, but the wholesale section sits below the stores section).
      var current = null;
      var currentTop = -Infinity;
      map.forEach(function (m) {
        var top = m.el.getBoundingClientRect().top;
        if (top <= 120 && top > currentTop) {
          current = m;
          currentTop = top;
        }
      });
      map.forEach(function (m) {
        m.link.classList.toggle("is-active", m === current);
      });
      setPageActive(current === null);
    }

    window.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync, { passive: true });
    sync();

    map.forEach(function (m) {
      m.link.addEventListener("click", function (e) {
        // The target section exists on THIS page — scroll to it in place
        // instead of navigating to the homepage anchor (handles links
        // whose href points at "/#id" while the section is local).
        e.preventDefault();
        setPageActive(false);
        map.forEach(function (x) {
          x.link.classList.toggle("is-active", x === m);
        });
        m.el.scrollIntoView({ behavior: "smooth", block: "start" });
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, "", m.link.hash);
        }
      });
    });
  }

  // Header logo, HOME link, and footer logo all resolve "home" to the same
  // place: the hero's last beat — the moment just before the broadcast bar
  // brings the nav down — instead of a cold top-of-page.
  //
  // - ON the homepage (scroll-scrubbed hero in the DOM): intercept the click
  //   and smooth-scroll there in place (no reload).
  // - On an interior page (no hero): can't scroll a page that isn't loaded
  //   yet, so arm a one-shot sessionStorage flag and let the normal
  //   navigation to "/" proceed; the homepage consumes the flag on load and
  //   lands at the same spot. Same destination, just across a navigation.
  var HOME_JUMP_KEY = "kf_home_to_last_beat";

  function initHomeJump(nav) {
    var hero = document.querySelector("[data-scroll-scrub-hero]");

    var brand = nav.querySelector(".kf-sitenav__brand");
    var homePath = brand ? brand.pathname : "/";

    // A link "goes home" if it resolves to the site root with no hash. That
    // guard naturally excludes FLOWER (different path) and the hashed jump
    // links, and picks up the footer BRAND-column "Home|/" link too.
    function isHomeLink(link) {
      return (
        link.pathname === homePath &&
        link.hash === "" &&
        link.origin === window.location.origin
      );
    }

    var targets = [];
    if (brand) targets.push(brand); // header logo

    // Header HOME link.
    Array.prototype.forEach.call(
      document.querySelectorAll(".kf-sitenav__link"),
      function (link) {
        if (isHomeLink(link)) targets.push(link);
      }
    );

    var footerBrand = document.querySelector(".kf-footer__brand a");
    if (footerBrand) targets.push(footerBrand); // footer logo

    // Footer nav "Home" text link (BRAND column, "Home|/").
    Array.prototype.forEach.call(
      document.querySelectorAll(".kf-footer__nav a"),
      function (link) {
        if (isHomeLink(link)) targets.push(link);
      }
    );

    if (!targets.length) return;

    function setFlag() {
      try { sessionStorage.setItem(HOME_JUMP_KEY, "1"); } catch (e) {}
    }
    function takeFlag() {
      var v = null;
      try {
        v = sessionStorage.getItem(HOME_JUMP_KEY);
        if (v) sessionStorage.removeItem(HOME_JUMP_KEY);
      } catch (e) {}
      return !!v;
    }

    // ── Interior page: arm the cross-page jump, then navigate normally ──
    if (!hero) {
      targets.forEach(function (link) {
        link.addEventListener("click", function () {
          setFlag(); // no preventDefault — let the link go to "/"
        });
      });
      return;
    }

    // ── Homepage ──────────────────────────────────────────────────────
    var motionOK = !(
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );

    function heroLastBeat() {
      // The CH 01 marker lives inside .kf-hero__sticky (position: sticky;
      // height = one viewport). While that element is "stuck" the marker is
      // parked top-right; the instant it releases, the whole hero slides up
      // and the marker is gone. The sticky releases when the hero section's
      // bottom reaches the sticky's bottom, i.e. at:
      //   heroTop + heroHeight - stickyHeight
      // Anchor to that — NOT to .kf-testcard. The test card is a separate
      // section below the hero, so its measured top includes the section
      // wrapper boxes and sits *past* this release point; that gap (not the
      // frame count) is why a small backoff never revealed the marker.
      var heroTop = hero.getBoundingClientRect().top + window.pageYOffset;
      var sticky = hero.querySelector(".kf-hero__sticky");
      var stickyH = sticky ? sticky.offsetHeight : window.innerHeight;
      var release = heroTop + hero.offsetHeight - stickyH;

      // Land one hero frame before release so the marker is still solidly
      // parked, not mid-slide. "One frame" = one slot of the stuck runway
      // over the frame sequence (count read from the DOM, so this stays
      // decoupled from home-scroll.js internals).
      var runway = Math.max(1, hero.offsetHeight - stickyH);
      var frameCount = 81;
      var srcsEl = hero.querySelector("[data-scroll-frame-sequence-srcs]");
      if (srcsEl) {
        try {
          var arr = JSON.parse(srcsEl.textContent || "[]");
          if (arr.length > 1) frameCount = arr.length;
        } catch (e) {}
      }
      var oneFrame = runway / (frameCount - 1);
      return Math.max(0, release - oneFrame);
    }

    // Arrived here via a HOME click on another page — finish the journey.
    // The age gate is once-per-session: same-session navigation skips it
    // (data-intro="skip"). If the gate IS showing (fresh visit), it owns
    // the entry and force-scrolls to top itself — let it win; just clear
    // the flag so it can't leak into a later in-session navigation.
    if (takeFlag()) {
      if (document.documentElement.getAttribute("data-intro") !== "show") {
        var land = function () {
          window.scrollTo({ top: heroLastBeat(), behavior: "auto" });
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, "", homePath);
          }
        };
        // After load + a frame so the hero's fixed runway and any
        // ScrollTrigger.refresh have settled before we position.
        if (document.readyState === "complete") {
          window.requestAnimationFrame(land);
        } else {
          window.addEventListener("load", function () {
            window.requestAnimationFrame(land);
          }, { once: true });
        }
      }
    }

    // In-place jump for clicks made while already on the homepage.
    targets.forEach(function (link) {
      link.addEventListener("click", function (e) {
        e.preventDefault();
        window.scrollTo({
          top: heroLastBeat(),
          behavior: motionOK ? "smooth" : "auto"
        });
        // Strip any stale section hash from an earlier jump-nav click so the
        // URL reads as the homepage again. replaceState (not push) keeps the
        // back button clean. The scroll spy restores HOME's .is-active via
        // the scroll events this scroll generates.
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, "", homePath);
        }
      });
    });
  }

  // Mobile drawer: the .kf-sitenav__links <ul> doubles as a right-side
  // off-canvas panel at <=768px (CSS owns the presentation). This wires
  // the hamburger, scrim, Esc, a focus trap, and the body scroll-lock
  // (same lock the shared lightbox uses). Bound AFTER initSpy so the
  // per-link close listeners are additive — no preventDefault, so the
  // spy's smooth-scroll / home-jump still fires.
  function initDrawer(nav) {
    // Burger is hoisted out of <nav> (escapes the nav's transform
    // stacking context so it can stay above the scrim).
    var toggle = document.querySelector("[data-nav-toggle]");
    // Drawer is hoisted out of <nav> (so it can escape the nav's
    // transform stacking context and layer above the scrim). Look it up
    // document-wide; aria-controls on the burger still references it.
    var drawer = document.querySelector("#kf-nav-drawer");
    // Document-scoped: the scrim is rendered as a sibling of <nav>, not
    // a child, so it can escape the nav's transform containing block and
    // cover the full viewport. nav.querySelector(...) would return null.
    var scrim = document.querySelector("[data-nav-scrim]");
    if (!toggle || !drawer) return;

    var lastFocused = null;
    var mq = window.matchMedia("(max-width: 768px)");

    function focusables() {
      var list = [toggle];
      Array.prototype.forEach.call(
        drawer.querySelectorAll("a[href], button:not([disabled])"),
        function (el) { list.push(el); }
      );
      return list;
    }
    function isOpen() {
      return drawer.classList.contains("is-open");
    }

    function openDrawer() {
      lastFocused = document.activeElement;
      drawer.classList.add("is-open");
      if (scrim) scrim.classList.add("is-open");
      toggle.setAttribute("aria-expanded", "true");
      toggle.setAttribute("aria-label", "Close menu");
      document.documentElement.style.overflow = "hidden";
      var f = focusables();
      (f[1] || toggle).focus();
    }
    function closeDrawer(restoreFocus) {
      drawer.classList.remove("is-open");
      if (scrim) scrim.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-label", "Open menu");
      // Only release the scroll lock if the shared lightbox isn't also
      // holding it (both use the same documentElement.overflow lock).
      if (!document.querySelector(".kf-lightbox.is-open")) {
        document.documentElement.style.overflow = "";
      }
      if (
        restoreFocus !== false &&
        lastFocused &&
        typeof lastFocused.focus === "function"
      ) {
        lastFocused.focus();
      }
    }

    toggle.addEventListener("click", function () {
      if (isOpen()) closeDrawer();
      else openDrawer();
    });
    if (scrim) {
      scrim.addEventListener("click", function () { closeDrawer(); });
    }

    // Tapping any drawer entry closes it. No preventDefault — the spy /
    // home-jump handlers (bound earlier) still run and smooth-scroll, so
    // don't yank focus back to the burger afterward.
    Array.prototype.forEach.call(
      drawer.querySelectorAll("a.kf-sitenav__link, .kf-sitenav__cart"),
      function (el) {
        el.addEventListener("click", function () {
          if (isOpen()) closeDrawer(false);
        });
      }
    );

    document.addEventListener("keydown", function (e) {
      if (!isOpen()) return;
      if (e.key === "Escape") { closeDrawer(); return; }
      if (e.key === "Tab") {
        var f = focusables();
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Grew past the drawer breakpoint while open → the <ul> reverts to
    // the desktop bar; drop the lock + open state so nothing is stuck.
    function onMqChange() {
      if (!mq.matches && isOpen()) closeDrawer(false);
    }
    if (mq.addEventListener) mq.addEventListener("change", onMqChange);
    else if (mq.addListener) mq.addListener(onMqChange);
  }

  // Cross-page hash landing. The head-script in layout/theme.liquid
  // strips the hash off the URL before the browser does its native
  // scroll-on-parse and stashes the original on window. Here we do ONE
  // instant scroll at DOMContentLoaded, restore the hash to the URL,
  // and let modern browser scroll anchoring keep the section visually
  // stable as lazy images above paint in. A backup rescroll at
  // window.load handles any residual drift.
  function initHashLand() {
    var hash = window.__kfPendingHash;
    if (!hash) return;

    function unhide() {
      document.documentElement.classList.remove("kf-hash-pending");
    }

    // Intro-gate owns fresh-visit flow — let it run, no landing.
    if (document.documentElement.getAttribute("data-intro") === "show") {
      unhide();
      return;
    }

    var target;
    try { target = document.querySelector(hash); } catch (e) {}
    if (!target) {
      unhide();
      return;
    }

    target.scrollIntoView({ behavior: "instant", block: "start" });
    try {
      history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search + hash
      );
    } catch (e) {}

    // Two RAFs so the scroll position is painted before we unhide —
    // prevents any flash of an intermediate scroll position.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(unhide);
    });

    // Backup: after all resources have loaded, re-scroll once in case
    // late-loading assets shifted layout.
    window.addEventListener("load", function () {
      target.scrollIntoView({ behavior: "instant", block: "start" });
    }, { once: true });
  }

  function init() {
    var nav = document.querySelector("[data-sitenav]");
    if (!nav) return;

    initSpy(nav);
    initHomeJump(nav);
    initHashLand();
    initDrawer(nav);

    // Interior pages render the nav already revealed + static (no hero
    // to hide behind). Skip the reveal observer entirely there.
    if (nav.classList.contains("kf-sitenav--static")) {
      nav.classList.add("is-revealed");
      return;
    }

    // Mobile: the test_card_1 wrapper is `position: sticky; bottom: 0` so
    // the broadcast bar is visible at the bottom of the viewport from page
    // load. Reveal the nav immediately to match — no IntersectionObserver
    // needed (and using the IO here would couple desktop reveal to the
    // mobile sticky state, which has caused false-positive reveals on
    // desktop in the past).
    var isMobile =
      window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
    if (isMobile) {
      nav.classList.add("is-revealed");
      return;
    }

    // Desktop: the broadcast bar is the first test-card strip, which sits
    // in natural flow directly below the 175vh hero. Tracking it means the
    // nav pops down exactly when the hero scroll is finished and the bar
    // shows — and stays for the rest of the page.
    var bar = document.querySelector(".kf-testcard");

    // Fail open: if there is no bar to track, just show the nav.
    if (!bar || typeof IntersectionObserver === "undefined") {
      nav.classList.add("is-revealed");
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Reveal once the broadcast bar is in view, or has scrolled
          // above the viewport (we're past it). Keep the nav hidden only
          // while the bar is still below the fold — i.e. still in the hero.
          var revealed =
            entry.isIntersecting || entry.boundingClientRect.bottom < 0;
          nav.classList.toggle("is-revealed", revealed);
        });
      },
      { threshold: 0 }
    );

    observer.observe(bar);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
